// Vypocet faktury – DPH per radek, sumace seskupene podle sazby.
// Pro presnost pracujeme interne v halerich (integer), v rozhrani vracime Kc (Number).

/**
 * @typedef {Object} LineItem
 * @property {string} id
 * @property {string} name
 * @property {number} qty       mnozstvi
 * @property {string} unit      m. j. (ks, hod, ...)
 * @property {number} price     jednotkova cena bez DPH (Kc)
 * @property {number} vat       sazba DPH v procentech (21, 12, 0)
 * @property {string} note
 */

/**
 * @typedef {Object} LineTotals
 * @property {number} base   zaklad bez DPH (Kc)
 * @property {number} vat    castka DPH (Kc)
 * @property {number} total  celkem s DPH (Kc)
 */

/**
 * @typedef {Object} VatGroup
 * @property {number} rate
 * @property {number} base
 * @property {number} vat
 * @property {number} total
 */

/**
 * @typedef {Object} InvoiceTotals
 * @property {VatGroup[]} groups   jeden zaznam per pouzita sazba DPH
 * @property {number} base
 * @property {number} vat
 * @property {number} total
 */

/**
 * Vrati zaklad / DPH / celkem pro jeden radek.
 * Nevalidni vstup (NaN qty/price) -> vse 0.
 * @param {LineItem} item
 * @returns {LineTotals}
 */
export function lineTotals(item) {
  const qty = Number(item.qty);
  const price = Number(item.price);
  const rate = Number(item.vat);

  if (!Number.isFinite(qty) || !Number.isFinite(price)) {
    return { base: 0, vat: 0, total: 0 };
  }

  // Pocitame v halerich (zaokrouhleni az pri vystupu).
  const baseHal = Math.round(qty * price * 100);
  const safeRate = Number.isFinite(rate) ? rate : 0;
  const vatHal = Math.round((baseHal * safeRate) / 100);
  const totalHal = baseHal + vatHal;

  return {
    base: baseHal / 100,
    vat: vatHal / 100,
    total: totalHal / 100,
  };
}

/**
 * Vrati sumace cele faktury seskupene podle sazby DPH.
 * @param {LineItem[]} items
 * @returns {InvoiceTotals}
 */
export function invoiceTotals(items) {
  const map = new Map();
  let baseHal = 0;
  let vatHal = 0;
  let totalHal = 0;

  for (const item of items) {
    const t = lineTotals(item);
    const rate = Number.isFinite(Number(item.vat)) ? Number(item.vat) : 0;

    const lineBaseHal = Math.round(t.base * 100);
    const lineVatHal = Math.round(t.vat * 100);
    const lineTotalHal = lineBaseHal + lineVatHal;

    baseHal += lineBaseHal;
    vatHal += lineVatHal;
    totalHal += lineTotalHal;

    const cur = map.get(rate) ?? { rate, baseHal: 0, vatHal: 0, totalHal: 0 };
    cur.baseHal += lineBaseHal;
    cur.vatHal += lineVatHal;
    cur.totalHal += lineTotalHal;
    map.set(rate, cur);
  }

  const groups = [...map.values()]
    .sort((a, b) => b.rate - a.rate)
    .map((g) => ({
      rate: g.rate,
      base: g.baseHal / 100,
      vat: g.vatHal / 100,
      total: g.totalHal / 100,
    }));

  return {
    groups,
    base: baseHal / 100,
    vat: vatHal / 100,
    total: totalHal / 100,
  };
}
